import React, { useState, useEffect } from 'react';
import { doc, getDoc, collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../firebase';

function YearOverYearView({ user, selectedYear, months }) {
  const [yoyData, setYoyData] = useState({ income: [], expense: [], savings: [] });
  const [loading, setLoading] = useState(true);
  const previousYear = selectedYear - 1;

  useEffect(() => {
    loadYearOverYearData();
  }, [selectedYear]);

  // Format number with thousand separators and brackets for negatives
  const formatNumber = (num, decimals = 2) => {
    const isNegative = num < 0;
    const absNum = Math.abs(num);
    const formatted = absNum.toLocaleString('en-GB', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    });
    return isNegative ? `(${formatted})` : formatted;
  };

  const loadYearOverYearData = async () => {
    setLoading(true);
    
    try {
      // Load categories
      const incomeSnapshot = await getDocs(collection(db, 'users', user.uid, 'income_categories'));
      const expenseSnapshot = await getDocs(collection(db, 'users', user.uid, 'expense_categories'));
      
      const incomeCategories = incomeSnapshot.docs.map(doc => doc.data().name?.trim()).filter(n => n).sort();
      const expenseCategories = expenseSnapshot.docs.map(doc => doc.data().name?.trim()).filter(n => n).sort();
      
      // Load savings accounts
      const q = query(
        collection(db, 'users', user.uid, 'account_setup'),
        where('active', '==', true)
      );
      const savingsSnapshot = await getDocs(q);
      const savingsAccounts = [];
      
      savingsSnapshot.docs.forEach(doc => {
        const data = doc.data();
        const accountName = data.name || '';
        const accountGroup = data.group || '';
        const isSavings = accountGroup.toLowerCase().includes('saving');
        
        if (isSavings && accountName) {
          savingsAccounts.push(accountName);
        }
      });
      
      const savingsCategories = savingsAccounts.sort();
      
      // Load all 12 months for both years
      const currentYearPromises = [];
      const previousYearPromises = [];
      
      for (let m = 0; m < 12; m++) {
        const monthStr = String(m + 1).padStart(2, '0');
        const currentYearDocId = `budget_${selectedYear}_${monthStr}`;
        const previousYearDocId = `budget_${previousYear}_${monthStr}`;
        
        currentYearPromises.push(getDoc(doc(db, 'users', user.uid, 'Budgets', currentYearDocId)));
        previousYearPromises.push(getDoc(doc(db, 'users', user.uid, 'Budgets', previousYearDocId)));
      }
      
      const [currentYearSnapshots, previousYearSnapshots] = await Promise.all([
        Promise.all(currentYearPromises),
        Promise.all(previousYearPromises)
      ]);
      
      const currentYearData = currentYearSnapshots.map(snap => snap.exists() ? snap.data() : {});
      const previousYearData = previousYearSnapshots.map(snap => snap.exists() ? snap.data() : {});
      
      // Build income YoY data - keep original values
      const incomeData = incomeCategories.map(category => {
        const currentYearMonthly = currentYearData.map(data => data.income?.[category]?.actual || 0);
        const previousYearMonthly = previousYearData.map(data => data.income?.[category]?.actual || 0);
        
        const currentYearTotal = currentYearMonthly.reduce((sum, val) => sum + val, 0);
        const previousYearTotal = previousYearMonthly.reduce((sum, val) => sum + val, 0);
        const variance = previousYearTotal !== 0 ? ((currentYearTotal - previousYearTotal) / Math.abs(previousYearTotal)) * 100 : 0;
        const difference = currentYearTotal - previousYearTotal;
        
        return {
          category,
          currentYearMonthly,
          previousYearMonthly,
          currentYearTotal,
          previousYearTotal,
          difference,
          variance
        };
      });
      
      // Build expense YoY data - keep original values (negative)
      const expenseData = expenseCategories.map(category => {
        const currentYearMonthly = currentYearData.map(data => data.expense?.[category]?.actual || 0);
        const previousYearMonthly = previousYearData.map(data => data.expense?.[category]?.actual || 0);
        
        const currentYearTotal = currentYearMonthly.reduce((sum, val) => sum + val, 0);
        const previousYearTotal = previousYearMonthly.reduce((sum, val) => sum + val, 0);
        const variance = previousYearTotal !== 0 ? ((currentYearTotal - previousYearTotal) / Math.abs(previousYearTotal)) * 100 : 0;
        const difference = currentYearTotal - previousYearTotal;
        
        return {
          category,
          currentYearMonthly,
          previousYearMonthly,
          currentYearTotal,
          previousYearTotal,
          difference,
          variance
        };
      });
      
      // Build savings YoY data
      const savingsData = savingsCategories.map(category => {
        const currentYearMonthly = currentYearData.map(data => data.savings?.[category]?.actual || 0);
        const previousYearMonthly = previousYearData.map(data => data.savings?.[category]?.actual || 0);
        
        const currentYearTotal = currentYearMonthly.reduce((sum, val) => sum + val, 0);
        const previousYearTotal = previousYearMonthly.reduce((sum, val) => sum + val, 0);
        const variance = previousYearTotal !== 0 ? ((currentYearTotal - previousYearTotal) / Math.abs(previousYearTotal)) * 100 : 0;
        const difference = currentYearTotal - previousYearTotal;
        
        return {
          category,
          currentYearMonthly,
          previousYearMonthly,
          currentYearTotal,
          previousYearTotal,
          difference,
          variance
        };
      });
      
      setYoyData({
        income: incomeData,
        expense: expenseData,
        savings: savingsData
      });
      
    } catch (error) {
      console.error('Error loading year-over-year data:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderYoYTable = (data, title, colorClass) => {
    if (data.length === 0) return null;
    
    const currentYearGrandTotal = data.reduce((sum, item) => sum + item.currentYearTotal, 0);
    const previousYearGrandTotal = data.reduce((sum, item) => sum + item.previousYearTotal, 0);
    const grandDifference = currentYearGrandTotal - previousYearGrandTotal;
    const grandVariance = previousYearGrandTotal !== 0 ? ((currentYearGrandTotal - previousYearGrandTotal) / Math.abs(previousYearGrandTotal)) * 100 : 0;
    
    const isExpense = colorClass === 'expense';
    
    return (
      <div className="yoy-section">
        <h3 className={`section-title ${colorClass}`}>{title}</h3>
        <div className="rolling-table-wrapper">
          <table className="rolling-table">
            <thead>
              <tr>
                <th className="sticky-col">CATEGORY</th>
                {months.map((month, index) => (
                  <th key={`curr-${index}`} className="center yoy-current">{month}<br/>{selectedYear}</th>
                ))}
                {months.map((month, index) => (
                  <th key={`prev-${index}`} className="center yoy-previous">{month}<br/>{previousYear}</th>
                ))}
                <th className="right">Total {selectedYear}</th>
                <th className="right">Total {previousYear}</th>
                <th className="right">DIFF</th>
                <th className="center">% VAR</th>
              </tr>
            </thead>
            <tbody>
              {data.map((item, index) => {
                // For expenses: positive difference = bad (spent more), negative = good (spent less)
                // For income: positive difference = good (earned more), negative = bad (earned less)
                const isGood = isExpense ? item.difference < 0 : item.difference > 0;
                const isBad = isExpense ? item.difference > 0 : item.difference < 0;
                
                return (
                  <tr key={index}>
                    <td className="sticky-col"><strong>{item.category}</strong></td>
                    {item.currentYearMonthly.map((val, mIndex) => (
                      <td key={`curr-${mIndex}`} className="right yoy-current">{val === 0 ? '-' : formatNumber(val, 0)}</td>
                    ))}
                    {item.previousYearMonthly.map((val, mIndex) => (
                      <td key={`prev-${mIndex}`} className="right yoy-previous">{val === 0 ? '-' : formatNumber(val, 0)}</td>
                    ))}
                    <td className="right"><strong>{formatNumber(item.currentYearTotal)}</strong></td>
                    <td className="right"><strong>{formatNumber(item.previousYearTotal)}</strong></td>
                    <td className={`right ${isGood ? 'positive' : isBad ? 'negative' : ''}`}>
                      {formatNumber(item.difference)}
                    </td>
                    <td className="center">
                      <span className={isGood ? 'variance-up' : isBad ? 'variance-down' : ''}>
                        {item.variance > 0 ? '↑ ' : item.variance < 0 ? '↓ ' : ''}
                        {Math.abs(item.variance).toFixed(1)}%
                      </span>
                    </td>
                  </tr>
                );
              })}
              <tr className="summary-row">
                <td className="sticky-col"><strong>TOTAL {title.toUpperCase()}</strong></td>
                {data[0]?.currentYearMonthly.map((_, mIndex) => {
                  const monthTotal = data.reduce((sum, item) => sum + item.currentYearMonthly[mIndex], 0);
                  return <td key={`curr-total-${mIndex}`} className="right yoy-current"><strong>{monthTotal === 0 ? '-' : formatNumber(monthTotal, 0)}</strong></td>;
                })}
                {data[0]?.previousYearMonthly.map((_, mIndex) => {
                  const monthTotal = data.reduce((sum, item) => sum + item.previousYearMonthly[mIndex], 0);
                  return <td key={`prev-total-${mIndex}`} className="right yoy-previous"><strong>{monthTotal === 0 ? '-' : formatNumber(monthTotal, 0)}</strong></td>;
                })}
                <td className="right"><strong>{formatNumber(currentYearGrandTotal)}</strong></td>
                <td className="right"><strong>{formatNumber(previousYearGrandTotal)}</strong></td>
                <td className={`right ${isExpense ? (grandDifference < 0 ? 'positive' : grandDifference > 0 ? 'negative' : '') : (grandDifference > 0 ? 'positive' : grandDifference < 0 ? 'negative' : '')}`}>
                  <strong>{formatNumber(grandDifference)}</strong>
                </td>
                <td className="center">
                  <strong className={isExpense ? (grandVariance < 0 ? 'variance-up' : grandVariance > 0 ? 'variance-down' : '') : (grandVariance > 0 ? 'variance-up' : grandVariance < 0 ? 'variance-down' : '')}>
                    {grandVariance > 0 ? '↑ ' : grandVariance < 0 ? '↓ ' : ''}
                    {Math.abs(grandVariance).toFixed(1)}%
                  </strong>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  if (loading) {
    return <div className="loading-state">Loading year-over-year data...</div>;
  }

  return (
    <div className="yoy-view">
      <h3>Year-over-Year: {selectedYear} vs {previousYear}</h3>
      {renderYoYTable(yoyData.income, 'Income', 'income')}
      {renderYoYTable(yoyData.savings, 'Savings', 'savings')}
      {renderYoYTable(yoyData.expense, 'Expenses', 'expense')}
    </div>
  );
}

export default YearOverYearView;